import { formatISO, parseISO } from 'date-fns';
import { COUNTRY_NAMES } from '@constants/countries';
import { DEFAULT_HOURS_INTERVAL } from '@constants/companies';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';

const hasDirtyLeaf = node =>
    node === true || (!!node && typeof node === 'object' && Object.values(node).some(hasDirtyLeaf));

export const locatedInForSubmit = (values, dirtyFields = {}) => {
    const touched = [dirtyFields.locatedIn, dirtyFields.locatedInEnabled, dirtyFields.hasBusinessAddress].some(
        hasDirtyLeaf,
    );
    if (!touched) {
        return null;
    }
    return values.hasBusinessAddress && values.locatedInEnabled && values.locatedIn?.placeId
        ? { ...values.locatedIn, relationType: values.locatedInRelationType }
        : {};
};

const phoneForSubmit = (number, storedPhones) =>
    storedPhones.find(stored => setInitialPhoneNumberToForm(stored) === number) ?? preparePhoneNumberForSubmit(number);

export const additionalPhonesForSubmit = (values, dirtyFields = {}, storedPhones = []) =>
    hasDirtyLeaf(dirtyFields.additionalPhones)
        ? values.additionalPhones.map(({ number }) => phoneForSubmit(number, storedPhones))
        : null;

export const hasOpenDay = workingHours =>
    (workingHours || []).some(day => Array.isArray(day?.hours) && day.hours.length > 0);

export const hasPublishedWorkingHours = (workingHoursEnabled, workingHours) =>
    Boolean(workingHoursEnabled) && hasOpenDay(workingHours);

const specialHourCalendarDay = hour => {
    if (!hour?.date) return null;
    const date = hour.date instanceof Date ? hour.date : new Date(hour.date);
    return Number.isNaN(date.getTime()) ? null : Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

export const hasFutureSpecialHour = specialHours => {
    const now = new Date();
    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return (specialHours || []).some(hour => specialHourCalendarDay(hour) >= todayUtc);
};

export const hasReversedSpecialRange = specialHours =>
    (specialHours || []).some(
        hour => !hour.isClosed && (hour.hours || []).some(({ from, to }) => to !== '24:00' && from >= to),
    );

export const hasDuplicateSpecialDate = specialHours => {
    const days = (specialHours || []).map(specialHourCalendarDay).filter(day => day !== null);
    return new Set(days).size !== days.length;
};

export const specialHoursToForm = specialHours =>
    (specialHours || []).map(hour => ({
        date: parseISO(hour.date),
        isClosed: !hour.isOpen,
        hours: hour.hours?.length ? hour.hours.map(({ from, to }) => ({ from, to })) : [{ ...DEFAULT_HOURS_INTERVAL }],
    }));

export const specialHoursForSubmit = specialHours =>
    (specialHours || []).map(hour => ({
        date: formatISO(hour.date, { representation: 'date' }),
        isOpen: !hour.isClosed,
        hours: hour.isClosed ? [] : hour.hours.map(({ from, to }) => ({ from, to })),
    }));

export const getCompanyAddress = (address, city) => {
    return [address, city].filter(Boolean).join(', ');
};

export const getCompanyFullAddress = company => {
    const addr1 = company.addressLine1 || '';
    const addr2 = company.addressLine2 || '';
    const city = company.city || '';
    const postalCode = company.postalCode || '';
    return `${addr1}${addr2 ? `, ${addr2}` : ''}${city ? `, ${city}` : ''}${postalCode ? `, ${postalCode}` : ''}`;
};

export const generateCompanyCardAddress = addr => {
    return `${addr.addressLines?.[0] || ''}, ${addr.city || ''}, ${addr.postalCode || ''}, ${
        COUNTRY_NAMES[addr.country] || ''
    }`;
};
